/**
 * Style of the customization text zone: position, scale and rotation of the
 * text, and the shadow of the thread.
 */
import type { CSSProperties } from 'react';

export const isDarkColor = (hex: string): boolean => {
  const rgb = Number.parseInt(hex.substring(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
  return luma < 60;
};

export interface TextProps {
  offsetX?: number | null | undefined;
  offsetY?: number | null | undefined;
  scale?: number | null | undefined;
  rotate?: number | null | undefined;
  letterSpace?: number | null | undefined;
  curve?: number | null | undefined;
}

const LEATHER_DARK =
  ' drop-shadow(rgba(255, 255, 255, 0.14) -0.02em 0em 0px) drop-shadow(rgba(255, 255, 255, 0.14) 0em -0.02em 0px) drop-shadow(rgba(255, 255, 255, 0.14) 0.03em 0em 0px) drop-shadow(rgba(255, 255, 255, 0.14) 0em 0.03em 0px)';
const LEATHER_LIGHT =
  ' drop-shadow(rgba(0,0,0,0.14) -0.02em 0em 0px) drop-shadow(rgba(0,0,0,0.14) 0em -0.02em 0px) drop-shadow(rgba(0,0,0,0.14) 0.03em 0em 0px) drop-shadow(rgba(0,0,0,0.14) 0em 0.03em 0px)';
const SHOES =
  ' drop-shadow(-0.01em -0.01em 0px rgba(0,0,0,0.15)) drop-shadow(0.015em 0.015em 0px rgba(0,0,0,0.15))';

export const getTextZoneStyle = ({
  textProps,
  color = '#ffffff',
  shadowType = 'flat',
  font = 'DINCondensed',
}: {
  textProps: TextProps | null | undefined;
  color?: string;
  shadowType?: string | null | undefined;
  font?: string;
}): CSSProperties => {
  const offsetX = textProps?.offsetX ?? 0;
  const offsetY = textProps?.offsetY ?? 0;
  const scale = textProps?.scale ?? 100;
  const rotate = textProps?.rotate ?? 0;
  const letterSpace = textProps?.letterSpace ?? 0;

  let shadowFilter = '';
  if (shadowType === 'leather')
    shadowFilter = isDarkColor(color) ? LEATHER_DARK : LEATHER_LIGHT;
  else if (shadowType === 'shoes') shadowFilter = SHOES;

  return {
    transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale / 100}) rotate(${rotate}deg)`,
    filter: `saturate(0) brightness(1.1) url(#textZoneFilter) ${shadowFilter}`,
    fontFamily: `${font}, DINCondensed, sans-serif`,
    letterSpacing: `${letterSpace}px`,
  };
};

/** Font family with its weight suffix, as the front office names its font files. */
export const fontWithWeight = (
  font: string | null | undefined,
  weight: string | null | undefined,
): string => {
  const family = font || 'DINCondensed';
  return weight === 'Regular' || !weight ? family : `${family}-${weight}`;
};

/** Colour matrix of the `#textZoneFilter` SVG filter for a given colour (white by default). */
export const filterMatrix = (
  rgb: [number, number, number] | undefined = [255, 255, 255],
) =>
  `${rgb[0] / 255} 0 0 0 0   0 ${rgb[1] / 255} 0 0 0   0 0 ${rgb[2] / 255} 0 0   0 0 0 1 0`;

const hexToRgbCache = new Map<string, [number, number, number] | null>();

/** `#rgb` or `#rrggbb` to its RGB components, null when not a colour. */
export const hexToRgb = (hex: string): [number, number, number] | null => {
  const cached = hexToRgbCache.get(hex);
  if (cached !== undefined) return cached;
  const expanded = hex.replace(
    /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
    (_match, r: string, g: string, b: string) => r + r + g + g + b + b,
  );
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(expanded);
  const rgb: [number, number, number] | null = result
    ? [
        Number.parseInt(result[1]!, 16),
        Number.parseInt(result[2]!, 16),
        Number.parseInt(result[3]!, 16),
      ]
    : null;
  hexToRgbCache.set(hex, rgb);
  return rgb;
};
